import clsx from 'clsx'
import {cloneDeep, has, isEmpty, isEqual, mapValues, omit, pick, some} from 'lodash'
import moment from 'moment'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useLocation, useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {convertUserTimeZone, KTSVG, StorageHelpers} from '../../../../../_gori/helpers'
import {OrderDetailSkeleton} from '../../../../../_gori/helpers/components'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import UseRoutesPermission from '../../../../../_gori/hooks/UseRoutesPermission'
import {
  Button,
  InputDate,
  InputTextArea,
  InputTextFormik,
  SelectFormik,
  SplitButtonDropdown,
  ValidationErrorModal,
} from '../../../../../_gori/partials/widgets'
import {useAuth} from '../../../auth'
import {OPTION_ITEM_TYPE} from '../../../claims/core/_const'
import {
  AddressValidationModal,
  CustomsInternational,
  EditAddress,
  ItemPackages,
  InsufficientBalanceModal,
  NoticeCreateLabelModal,
  PackageInfo,
  PresetApplyButton,
} from '../../../orders'
import ShipmentService from '../../../shipments/core/_requests'
import SettingsService from '../../../settings/core/_requests'
import {
  OrdersConfig,
  OrderStatus,
  OrderType,
  OrderChildModalName,
  OrderChildModalNameType,
  INSURANCE_TYPE,
} from '../../core/_const'
import OrderHelper from '../../core/_helpers'
import OrderService from '../../core/_requests'
import {useOrderChild} from '../../hooks'
import {
  OrderChildRate,
  OrderChildRateForwardRefProps,
} from '../../components/order-child/OrderChildRate'
import {isInternationalByAddress} from '../../components/order-child/rateHelpers'
import {Policy, PolicySource} from '../../../opt/core/_const'
import OptService from '../../../opt/core/_requests'
import Cookies from 'js-cookie/dist/js.cookie.mjs'
import {
  ACCEPT_TERM_UPIC_KEY,
  LAPTOP_MIN_WIDTH,
  RATE_SERVICES_KEY,
} from '../../../../../_gori/constants'
import {USPS_MANIFEST_SERVICES} from '../../../../../_gori/constants/_const'
import {ROUTES} from '../../../../../_gori/constants/_routes'
import useWindowDimensions from '../../../../../_gori/hooks/useWindowDimensions'

type Props = {
  show: boolean
  handleClose: () => void
  handleSuccess?: any
  parentId: any
  name?: OrderChildModalNameType
}

const OrderChildModal: React.FC<Props> = ({
  show,
  handleClose,
  handleSuccess,
  parentId,
  name = OrderChildModalName.CREATE_LABEL,
}) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const fromUrl: string | null = (location.state as any)?.fromUrl || null
  const orderHelper = OrderHelper()
  const {currentUser} = useAuth()
  const renderFirst = useRef(true)
  const {newCancelToken, isCancel} = useCancelToken()
  const {statesGlobal} = useStatesGlobal()
  const carriers = statesGlobal.carriers
  const [loading, setLoading] = useState<{
    validate: boolean
    first: boolean
    save: boolean
    ship: boolean
    rate: boolean
  }>({
    validate: false,
    first: true,
    save: false,
    ship: false,
    rate: false,
  })
  const [showModal, setShowModal] = useState<{
    noticeCreateLabel: boolean
    insufficientFunds: boolean
  }>({
    noticeCreateLabel: false,
    insufficientFunds: false,
  })
  const [insufficientBalanceLogId, setInsufficientBalanceLogId] = useState<
    string | number | undefined
  >()
  const [action, setAction] = useState<'validate' | 'save' | 'ship' | undefined>()
  const [immediateManifestFlag, setImmediateManifestFlag] = useState<boolean>(false)
  const [selectedMode, setSelectedMode] = useState<'label' | 'manifest'>('label')
  const [fromToModal, setFormToModal] = useState<any>({
    show: false,
    name: undefined,
    data: {},
    validate: {},
  })
  const [validationErrors, setValidationErrors] = useState<any>()
  const {routes} = UseRoutesPermission()
  const [canSave, setCanSave] = useState<boolean>(!parentId)
  const [checkValidateLeft, setCheckValidateLeft] = useState<boolean>(false)
  const currentValue = useRef<any>(null)
  const [showHide, setShowHide] = useState<{
    shipmentDetail: boolean
    custom: boolean
    orderDetail: boolean
  }>({shipmentDetail: false, custom: false, orderDetail: true})

  const [showValidationModal, setShowValidationModal] = useState(false)
  const [dataSuggest, setDataSuggest] = useState<any>()
  const validateTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const {width} = useWindowDimensions()
  const isTinyMobile = width < 375

  const onSubmit = useCallback(
    async (values) => {
      values = {
        ...values,
        ship_by_date:
          isEmpty(values.ship_by_date?.format()) || values.ship_by_date?.format() === 'Invalid date'
            ? undefined
            : values.ship_by_date?.format(),
      }

      if (action === 'save') {
        formik.setFieldValue('insurance_option.upic_terms_of_service', null)
        handleSaveOrders(values).catch(() => {})
      }

      if (action === 'validate') {
        handleValidateAddress(values)
      }

      if (action === 'ship') {
        handleCreateLabel(values)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [action]
  )
  const rateRef = useRef<OrderChildRateForwardRefProps>(null)

  const {
    row,
    validateAddress,
    validateSchema,
    data,
    formik,
    handleAddNewItem,
    handleDeleteItem,
    currentDimensionUnit,
    setCurrentDimensionUnit,
    currentWeightUnit,
    setCurrentWeightUnit,
    setScrollToErrors,
    isAccept,
    setIsAccept,
    isErrorUpicAccept,
    setIsErrorUpicAccept,
  } = useOrderChild({id: parentId, setLoading, onSubmit})

  const hasPermissionView =
    routes.ORDERS_UPDATE.hasPermission || routes.ORDERS_CREATE.hasPermission
      ? false
      : routes.ORDERS_VIEW.hasPermission

  const isStatusOpen = useMemo(() => {
    return data?.status === OrderStatus.OPEN
  }, [data?.status])

  const isDisablePage = useMemo(() => {
    return loading.save || loading.ship || loading.validate || !!data?.a1_batch_id
  }, [data?.a1_batch_id, loading.save, loading.ship, loading.validate])

  useEffect(() => {
    if (parentId) {
      const allValue = cloneDeep({...formik.values})
      currentValue.current = pick(allValue, [
        'from',
        'to',
        'parcels',
        'reference1',
        'reference2',
        'reference3',
        'claim_category',
        'ship_by_date',
        'order_items',
        'custom_items',
        'international_option',
        'signature_confirmation',
        'insurance_option',
        'note',
        'extra_service',
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.initialValues])

  useEffect(() => {
    return () => {
      StorageHelpers.removeItemLocalStorage(RATE_SERVICES_KEY)
    }
  }, [])

  // BEGIN: Immediate Manifest Setting
  const getManifestSetting = useCallback(async () => {
    try {
      const settings = await SettingsService.settings({cancelToken: newCancelToken()})
      setSelectedMode(settings?.manifest?.immediate_manifest ? 'manifest' : 'label')
    } catch (error) {
      if (isCancel(error)) return
    }
  }, [isCancel, newCancelToken])

  useEffect(() => {
    getManifestSetting()
    return () => {}
  }, [getManifestSetting])

  const lastIsUspsRef = useRef<boolean>(false)
  const isUsps = useMemo(() => {
    const service = formik.values?.service
    if (!service) return lastIsUspsRef.current
    const result = USPS_MANIFEST_SERVICES.includes(service)
    lastIsUspsRef.current = result
    return result
  }, [formik.values?.service])
  // END: Immediate Manifest Setting

  // BEGIN: Detail Order
  const handleSaveOrders = useCallback(
    async (values) => {
      try {
        currentValue.current = pick({...formik.values}, [
          'from',
          'to',
          'parcels',
          'reference1',
          'reference2',
          'reference3',
          'claim_category',
          'ship_by_date',
          'order_items',
          'custom_items',
          'international_option',
          'signature_confirmation',
          'insurance_option',
          'extra_service',
          'note',
        ])

        setLoading((prev) => ({...prev, save: true}))

        const dataParcels = orderHelper.buildParcels(cloneDeep(values?.parcels))
        const internationalOption = orderHelper.buildInternationalOption(
          values?.international_option,
          values?.custom_items
        )
        const res = await OrderService.update(
          parentId,
          {
            fields: {
              service: values.service ?? null,
              estimated_rate: values?.estimated_rate ?? null,
              is_cheapest_rate: values?.is_cheapest_rate ?? false,
              preset_id: values?.preset_id ?? null,
              signature_confirmation: values.signature_confirmation ?? null,
              extra_service: values?.extra_service ?? null,
              insurance_option: values.insurance_option,
              reference1: values.reference1,
              reference2: values.reference2,
              reference3: values.reference3,
              ship_by_date: values.ship_by_date,
              order_items: values?.order_items,
              international_option: values.international ? internationalOption : null,
              claim_category: values?.claim_category || null,
              note: values?.note || null,
              ...values.from,
              ...values.to,
              parcel: dataParcels,
            },
          },
          {cancelToken: newCancelToken()}
        )
        if (res) {
          switch (name) {
            case OrderChildModalName.CREATE_LABEL:
              toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
              rateRef.current?.handleGetRates()
              break
            case OrderChildModalName.EDIT_ORDER:
              handleSuccess()
              break
          }
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(
          error?.response || {
            data: {error: {message: intl.formatMessage({id: 'NETWORK_ERROR'})}},
          }
        )
        throw error
      } finally {
        setLoading((prev) => ({...prev, save: false}))
        setAction(undefined)
      }
    },
    [formik.values, handleSuccess, intl, isCancel, name, newCancelToken, orderHelper, parentId]
  )
  const handleCreateLabel = useCallback(
    async (values) => {
      if (
        values.insurance_option?.provider === INSURANCE_TYPE.BAE_GUARD &&
        isAccept &&
        !values.insurance_option?.upic_terms_of_service
      ) {
        setIsErrorUpicAccept(true)
        return
      }
      const _valueForm = cloneDeep(values)
      setLoading((prev) => ({...prev, ship: true}))

      const dataParcels = orderHelper.buildParcels(cloneDeep(_valueForm?.parcels))
      const internationalOption = orderHelper.buildInternationalOption(
        _valueForm?.international_option,
        _valueForm?.custom_items
      )

      const isReship = data?.status === OrderStatus.SHIPPED
      const payload = {
        id: data?.id,
        store_type: data?.store_type,
        store_id: data?.store_id,
        fields: {
          service: _valueForm.service,
          preset_id: values?.preset_id ?? null,
          delivery_option_id: _valueForm.delivery_option_id ?? null,
          signature_confirmation: _valueForm.signature_confirmation ?? null,
          extra_service: _valueForm?.extra_service ?? null,
          insurance_option: !!carriers[_valueForm?.service]?.options?.insurance.filter(
            (item) => item.enabled
          )?.length
            ? {
                ..._valueForm.insurance_option,
              }
            : null,
          reference1: _valueForm?.reference1,
          reference2: _valueForm?.reference2,
          reference3: _valueForm?.reference3,
          ship_by_date:
            isEmpty(_valueForm?.ship_by_date) || _valueForm?.ship_by_date === 'Invalid date'
              ? null
              : _valueForm?.ship_by_date,
          order_items: _valueForm?.order_items,
          international_option: _valueForm.international ? internationalOption : null,
          claim_category: _valueForm?.claim_category || null,
          note: _valueForm?.note || null,
          ..._valueForm.from,
          ..._valueForm.to,
          parcel: dataParcels,
          immediate_manifest: immediateManifestFlag ?? null,
        },
      }

      try {
        if (isAccept && values?.insurance_option?.upic_terms_of_service) {
          let payloadAcceptTerms = {
            policies: [Policy.U_PIC],
            source: PolicySource.SHIPBAE,
          }
          const responseAcceptTerms = await OptService.acceptTermsUPic(payloadAcceptTerms)
          if (responseAcceptTerms.data.status) {
            Cookies.set(ACCEPT_TERM_UPIC_KEY, false)
          }
        }
        let response: any
        if (payload.store_type === OrderType.TEMU) {
          response = isReship
            ? await ShipmentService.reshipTemu(payload, {cancelToken: newCancelToken()})
            : await ShipmentService.storeTemu(payload, {cancelToken: newCancelToken()})
        } else {
          response = isReship
            ? await ShipmentService.reship(payload, {cancelToken: newCancelToken()})
            : await ShipmentService.store(payload, {cancelToken: newCancelToken()})
        }
        if (response) {
          const navigateUrl = parentId
            ? (() => {
                if (fromUrl) {
                  const [pathname, search] = fromUrl.split('?')
                  const urlParams = new URLSearchParams(search || '')
                  urlParams.set('status', OrderStatus.SHIPPED)
                  urlParams.set('page', '1')
                  return `${pathname}?${urlParams.toString()}`
                }
                return '/orders?status=' + OrderStatus.SHIPPED
              })()
            : null
          handleSuccess(response.label_url, navigateUrl)
        }
      } catch (error: any) {
        if (isCancel(error)) return
        if (error?.response?.data?.error?.name === 'METER:INSUFFICIENT_BALANCE') {
          setInsufficientBalanceLogId(error?.response?.data?.error?.log_id)
          setShowModal((prev) => ({...prev, insufficientFunds: true}))
        } else {
          setValidationErrors(error?.response)
        }
      } finally {
        setLoading((prev) => ({...prev, ship: false}))
        setAction(undefined)
      }
    },
    [
      carriers,
      data?.id,
      data?.store_id,
      data?.store_type,
      data?.status,
      fromUrl,
      handleSuccess,
      immediateManifestFlag,
      isCancel,
      parentId,
      newCancelToken,
      orderHelper,
      isAccept,
      setIsErrorUpicAccept,
    ]
  )
  // END: Detail Order

  // BEGIN: Check international
  useEffect(() => {
    formik.setFieldValue(
      'international',
      isInternationalByAddress(formik.values?.from, formik.values?.to)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps, no-sparse-arrays
  }, [
    formik.values.from.from_country,
    formik.values.from.from_state,
    formik.values.from.from_city,
    formik.values.to.to_country,
    formik.values.to.to_state,
    formik.values.to.to_city,
  ])
  // END: Check international

  // BEGIN: Clear reference3/4 for non-domestic USPS
  useEffect(() => {
    const isDomesticUSPS =
      !formik.values.international && formik.values.service?.startsWith('usps_')
    if (!isDomesticUSPS) {
      if (formik.values.reference3 || formik.values.reference4) {
        formik.setFieldValue('reference3', null)
        formik.setFieldValue('reference4', null)
      }
    }
  }, [formik, formik.values.international, formik.values.service])
  // END: Clear reference3/4 for non-domestic USPS

  const objectValuesToString = (obj: any) => {
    return mapValues(obj, (value) => value?.toString?.() ?? '')
  }

  // BEGIN: Validate has can save
  useEffect(() => {
    const formattedParcels = Object.fromEntries(
      Object.entries(formik.values.parcels).map(([key, parcel]) => [
        key,
        objectValuesToString(orderHelper.formatParcels(parcel)),
      ])
    )

    const dataParcels = omit(
      pick(
        {
          ...formik.values,
          parcels: formattedParcels,
        },
        [
          'from',
          'to',
          'parcels',
          'reference1',
          'reference2',
          'reference3',
          'reference4',
          'claim_category',
          'ship_by_date',
          'order_items',
          'custom_items',
          'international_option',
          'signature_confirmation',
          'extra_service',
          'insurance_option',
          'note',
        ]
      ),
      'insurance_option.upic_terms_of_service'
    )

    const currentValueToCompare = {
      ...omit(currentValue.current, 'insurance_option.upic_terms_of_service'),
    }
    if (currentValue.current?.parcels) {
      Object.entries(currentValue.current?.parcels).forEach(([key, value]) => {
        currentValueToCompare.parcels[key] = objectValuesToString(value)
      })
    }

    if (parentId && (isEqual(dataParcels, currentValueToCompare) || !currentValue.current)) {
      return setCanSave(false)
    }
    setCanSave(checkValidateLeft)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkValidateLeft, formik.values, currentValue.current, parentId])

  useEffect(() => {
    const result = some(
      [
        'from',
        'to',
        'parcels',
        'custom_items',
        'international_option',
        'order_items',
        'check_weight',
        'check_package_count',
      ],
      (key) => has(formik.errors, key)
    )
    if (checkValidateLeft !== !result) {
      setCheckValidateLeft(!result)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.errors])
  // END: Validate has can save

  // BEGIN: Reset Validate
  useEffect(() => {
    validateTimeoutRef.current = setTimeout(() => {
      formik.validateForm()
    }, 800)

    return () => {
      if (validateTimeoutRef.current) {
        clearTimeout(validateTimeoutRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateSchema])
  // END: Reset Validate

  const handleCreateLabelModal = () => {
    setShowModal((prev) => ({...prev, noticeCreateLabel: false}))
    setAction('ship')
    setScrollToErrors(true)
    formik.handleSubmit()
  }

  const handleAddFunds = useCallback(async () => {
    try {
      // Shipped orders can't be updated via API — skip save and navigate directly
      if (data?.status !== OrderStatus.SHIPPED) {
        const values = {
          ...formik.values,
          ship_by_date:
            isEmpty(formik.values.ship_by_date?.format()) ||
            formik.values.ship_by_date?.format() === 'Invalid date'
              ? undefined
              : formik.values.ship_by_date?.format(),
        }
        formik.setFieldValue('insurance_option.upic_terms_of_service', null)
        await handleSaveOrders(values)
      }
      setShowModal((prev) => ({...prev, insufficientFunds: false}))
      navigate(ROUTES.DEPOSITS.BASE)
    } catch {
      // Save failed — close this modal so the validation error modal is visible
      setShowModal((prev) => ({...prev, insufficientFunds: false}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status, handleSaveOrders, formik.values, navigate])

  // END: Notice about package type & service in Create Label

  // BEGIN: JUST MODAL
  const handleCloseModal = useCallback(() => {
    handleClose()
    setAction(undefined)
    formik.resetForm()
    row.current = {parcels: 0, order_items: 0, custom_items: 0}
  }, [formik, handleClose, row])

  const handleUseSuggested = (key) => {
    const updateValue = OrdersConfig.VALIDATE_ADDRESS.reduce((initValue, current) => {
      initValue[`${key}_${current}`] = dataSuggest[key][current]
      return initValue
    }, {})
    updateValue[`${key}_is_verified`] = true

    formik.setFieldValue(key, {...formik.getFieldProps(key).value, ...updateValue})
  }

  const handleValidateAddress = useCallback(
    async (values) => {
      setLoading((prev) => ({...prev, validate: true}))
      try {
        const [valuePayloadFrom, valuePayloadTo] = ['from', 'to'].map((item) =>
          OrdersConfig.VALIDATE_ADDRESS.reduce((valueCurrent, current) => {
            valueCurrent[current] = values[item][`${item}_${current}`]
            return valueCurrent
          }, {})
        )

        const res = await Promise.all([
          OrderService.validateAddress(
            {address: valuePayloadFrom},
            {cancelToken: newCancelToken()}
          ),
          OrderService.validateAddress({address: valuePayloadTo}, {cancelToken: newCancelToken()}),
        ])
        const [valueSuggestFrom, valueSuggestTo] = [0, 1].map((index) =>
          OrdersConfig.VALIDATE_ADDRESS.reduce((initValue, current) => {
            initValue[current] = res[index][current]
            return initValue
          }, {})
        )
        formik.setFieldValue(
          'from.from_is_verified',
          isEqual(valuePayloadFrom, valueSuggestFrom) &&
            res[0].verifications.delivery.success &&
            res[0].verifications.zip4.success
        )
        formik.setFieldValue(
          'to.to_is_verified',
          isEqual(valuePayloadTo, valueSuggestTo) &&
            res[1].verifications.delivery.success &&
            res[1].verifications.zip4.success
        )

        setDataSuggest({from: res[0], to: res[1]})
        setShowValidationModal(true)
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, validate: false}))
        setAction(undefined)
      }
    },
    [formik, isCancel, newCancelToken]
  )
  // END: JUST MODAL

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}

      {showModal.noticeCreateLabel && (
        <NoticeCreateLabelModal
          show={showModal.noticeCreateLabel}
          handleClose={() => setShowModal((prev) => ({...prev, noticeCreateLabel: false}))}
          handleCreateLabel={handleCreateLabelModal}
          name={data?.status === OrderStatus.SHIPPED ? 'RESHIP' : 'CREATE_LABEL'}
        />
      )}

      {showModal.insufficientFunds && (
        <InsufficientBalanceModal
          show={showModal.insufficientFunds}
          handleClose={() => setShowModal((prev) => ({...prev, insufficientFunds: false}))}
          onAddFunds={handleAddFunds}
          logId={insufficientBalanceLogId}
        />
      )}

      {showValidationModal && (
        <AddressValidationModal
          show={showValidationModal}
          handleCloseValidation={() => setShowValidationModal(false)}
          handleUseSuggested={handleUseSuggested}
          dataSuggest={dataSuggest}
          dataOriginal={formik.values}
        />
      )}

      <Modal
        id='gori_modal_create_label'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='modal-size-xxl h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            {parentId && loading.first ? (
              <span className='fs-1 w-200px placeholder placeholder-lg rounded-2 bg-secondary me-2' />
            ) : (
              (() => {
                let labelModal: string | null = null
                switch (name) {
                  case OrderChildModalName.EDIT_ORDER:
                    labelModal = 'EDIT_ORDER'
                    break
                  case OrderChildModalName.CREATE_LABEL:
                    labelModal = data?.status === OrderStatus.SHIPPED ? 'RESHIP' : 'CREATE_LABEL'
                    break
                }
                return intl.formatMessage({id: labelModal})
              })()
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='vh-75 overflow-auto'>
          <>
            <>
              {parentId && loading.first ? (
                <OrderDetailSkeleton />
              ) : (
                (() => {
                  return (
                    <div>
                      <div
                        style={{gap: width >= LAPTOP_MIN_WIDTH ? '24px' : '0'}}
                        className={clsx('d-flex flex-row', {
                          'flex-column': width <= LAPTOP_MIN_WIDTH,
                        })}
                      >
                        <div className='z-index-1 col-12 col-xl-7'>
                          <div
                            className={clsx('bg-white rounded-2 border border-primary p-6', {
                              'bg-light cursor-no-drop': isDisablePage,
                            })}
                          >
                            <div className='d-flex justify-content-between align-items-center'>
                              <span className='fs-2 fw-bolder required'>
                                {intl.formatMessage({id: 'SHIPMENT_DETAIL'})}
                              </span>
                              <PresetApplyButton
                                formik={formik}
                                disabled={isDisablePage}
                                fromCreateOrder={false}
                                dataCopy={true}
                                setDataRates={rateRef.current?.setDataRates}
                                setPresetService={rateRef.current?.setPresetService}
                                renderFirst={renderFirst}
                              />
                            </div>
                            <div>
                              <hr className='bg-gray-600' />
                              <div>
                                <div>
                                  <div className='d-flex justify-content-between align-items-center'>
                                    <h3 className='fw-bolder fs-2'>
                                      {intl.formatMessage({id: 'SHIP_FROM'})}
                                    </h3>
                                    <div
                                      className='d-flex align-items-center btn btn-advanced-tertiary fs-inherit py-0 px-1 fw-bolder cursor-pointer'
                                      onClick={() => {
                                        setFormToModal({
                                          show: true,
                                          name: 'from',
                                          data: formik.getFieldProps('from').value,
                                          validate: validateAddress('from'),
                                        })
                                      }}
                                    >
                                      <span className='fs-2 me-1'>+</span>
                                      {intl.formatMessage({
                                        id: Object.entries(formik.getFieldProps('from').value).some(
                                          ([key, value]) => !isEmpty(value)
                                        )
                                          ? 'EDIT_ADDRESS'
                                          : 'NEW_ADDRESS',
                                      })}
                                    </div>
                                  </div>
                                  <EditAddress
                                    formik={formik}
                                    fromToModal={fromToModal}
                                    handleOpenFromToModal={(name, value) =>
                                      setFormToModal({
                                        show: true,
                                        name,
                                        data: value,
                                        validate: validateAddress(name),
                                      })
                                    }
                                    handleCloseFromToModal={() =>
                                      setFormToModal({
                                        show: false,
                                        name: undefined,
                                        data: {},
                                        validate: {},
                                      })
                                    }
                                    name='from'
                                    disabled={isDisablePage || hasPermissionView}
                                  />
                                </div>
                                <div className='mt-4'>
                                  <div className='d-flex justify-content-between align-items-center'>
                                    <h3 className='fw-bolder fs-2'>
                                      {intl.formatMessage({id: 'SHIP_TO'})}
                                    </h3>
                                    <div
                                      className='d-flex align-items-center btn btn-advanced-tertiary fs-inherit py-0 px-1 fw-bolder cursor-pointer'
                                      onClick={() => {
                                        setFormToModal({
                                          show: true,
                                          name: 'to',
                                          data: formik.values.to,
                                          validate: validateAddress('to'),
                                        })
                                      }}
                                    >
                                      <span className='fs-2 me-1'>+</span>
                                      {intl.formatMessage({
                                        id: Object.entries(formik.values.to).some(
                                          ([key, value]) => !isEmpty(value)
                                        )
                                          ? 'EDIT_ADDRESS'
                                          : 'NEW_ADDRESS',
                                      })}
                                    </div>
                                  </div>
                                  <EditAddress
                                    formik={formik}
                                    fromToModal={fromToModal}
                                    handleOpenFromToModal={(name, value) =>
                                      setFormToModal({
                                        show: true,
                                        name,
                                        data: value,
                                        validate: validateAddress(name),
                                      })
                                    }
                                    handleCloseFromToModal={() =>
                                      setFormToModal({
                                        show: false,
                                        name: undefined,
                                        data: {},
                                        validate: {},
                                      })
                                    }
                                    name='to'
                                    disabled={isDisablePage || hasPermissionView}
                                  />
                                </div>
                              </div>
                              <div>
                                <PackageInfo
                                  formik={formik}
                                  disabled={isDisablePage || hasPermissionView}
                                  handleAddNewItem={handleAddNewItem}
                                  handleDeleteItem={handleDeleteItem}
                                  currentDimensionUnit={currentDimensionUnit}
                                  setCurrentDimensionUnit={setCurrentDimensionUnit}
                                  currentWeightUnit={currentWeightUnit}
                                  setCurrentWeightUnit={setCurrentWeightUnit}
                                  isInModal
                                />
                              </div>
                              <div>
                                <div className='d-flex flex-wrap gap-2 justify-content-between mt-10 mb-2'>
                                  <div className='fs-3 fw-bolder'>
                                    {intl.formatMessage({id: 'SHIPMENT_DATE'})}
                                  </div>
                                  <div
                                    className={clsx('d-flex col-12 col-sm-6', {
                                      'cursor-no-drop': isDisablePage || hasPermissionView,
                                    })}
                                  >
                                    <InputDate
                                      className={clsx('col-12', {'pe-none': isDisablePage})}
                                      labelClassName='col-3 col-form-label text-nowrap text-muted'
                                      name='ship_by_date'
                                      formik={formik}
                                      min={moment()}
                                      disabled={isDisablePage || hasPermissionView}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          {formik.values.international && (
                            <div className='mt-10'>
                              <CustomsInternational
                                formik={formik}
                                handleAddNewItem={handleAddNewItem}
                                handleDeleteItem={handleDeleteItem}
                                disabled={isDisablePage || hasPermissionView}
                                isShippedPage={false}
                                showHide={showHide}
                                setShowHide={setShowHide}
                              />
                            </div>
                          )}
                          <div
                            className={clsx('bg-light rounded-2 mt-10 p-6', {
                              'bg-light cursor-no-drop': isDisablePage,
                            })}
                          >
                            <div className='d-flex justify-content-between align-items-center'>
                              <span className='fs-2 fw-bolder'>
                                {intl.formatMessage({id: 'ORDER_DETAIL'})}
                              </span>
                            </div>
                            <div>
                              <hr className='bg-gray-600' />
                              <div className='d-flex flex-wrap gap-2'>
                                <div className='col-12 col-sm-6 pe-sm-6'>
                                  <label className='form-label text-muted col-12 col-sm-4'>
                                    {intl.formatMessage({id: 'ORDER_DATE'})}
                                  </label>
                                  <span className='col-12 col-sm-8 text-dark fw-bolder'>
                                    {!parentId
                                      ? moment().format(process.env.REACT_APP_DATE_FORMAT)
                                      : convertUserTimeZone(
                                          data?.created_at,
                                          currentUser,
                                          process.env.REACT_APP_DATE_FORMAT
                                        )}
                                  </span>
                                </div>
                                <div className='col-12 col-sm-6 ps-sm-6'>
                                  <label className='form-label text-muted col-12 col-sm-4'>
                                    BAE ID
                                  </label>
                                  <span className='col-12 col-sm-8 text-dark fw-bolder'>
                                    {data && (
                                      <>
                                        <span className='fw-bolder text-dark'>{data?.code}</span>
                                      </>
                                    )}
                                  </span>
                                </div>
                              </div>
                              <div className='d-flex justify-content-between flex-wrap'>
                                <div
                                  className={clsx('col-12 col-sm-6 pe-sm-6', {
                                    'd-flex flex-wrap': !isDisablePage,
                                  })}
                                >
                                  <InputTextFormik
                                    className={clsx('col-12 col-sm-8', {'col-12': isDisablePage})}
                                    labelClassName='col-12 col-sm-4 col-form-label text-nowrap text-muted'
                                    label={intl.formatMessage({id: 'REF_1'})}
                                    name='reference1'
                                    formik={formik}
                                    disabled={hasPermissionView || isDisablePage}
                                  />
                                </div>
                                <div
                                  className={clsx('col-12 col-sm-6 ps-sm-6', {
                                    'd-flex flex-wrap': !isDisablePage,
                                  })}
                                >
                                  <InputTextFormik
                                    className={clsx('col-12 col-sm-8', {'col-12': isDisablePage})}
                                    labelClassName='col-12 col-sm-4 col-form-label text-nowrap text-muted'
                                    label={intl.formatMessage({id: 'REF_2'})}
                                    name='reference2'
                                    formik={formik}
                                    disabled={hasPermissionView || isDisablePage}
                                  />
                                </div>
                              </div>
                              {!formik.values.international &&
                                formik.values.service?.startsWith('usps_') && (
                                  <div className='d-flex justify-content-between mt-4'>
                                    <div
                                      className={clsx('col-12 col-sm-6 pe-6', {
                                        'd-flex flex-wrap': !isDisablePage,
                                      })}
                                    >
                                      <InputTextFormik
                                        className={clsx('col-12 col-sm-8', {
                                          'col-12': isDisablePage,
                                        })}
                                        labelClassName='col-12 col-sm-4 col-form-label text-nowrap text-muted'
                                        label={intl.formatMessage({id: 'REF_3'})}
                                        name='reference3'
                                        formik={formik}
                                        disabled={hasPermissionView || isDisablePage}
                                      />
                                    </div>
                                  </div>
                                )}
                              <div className={clsx('mt-4', {'d-flex flex-wrap': !isDisablePage})}>
                                <SelectFormik
                                  className={clsx('col-12 col-sm-8', {'col-12': isDisablePage})}
                                  labelClassName='col-12 col-sm-4 col-form-label text-nowrap text-muted'
                                  label={intl.formatMessage({id: 'MERCHANDISE_CATEGORY'})}
                                  placeholder={intl.formatMessage(
                                    {id: 'SELECT_A_INPUT'},
                                    {input: intl.formatMessage({id: 'MERCHANDISE_CATEGORY'})}
                                  )}
                                  options={OPTION_ITEM_TYPE}
                                  formik={formik}
                                  name={'claim_category'}
                                  hasUseIntl={true}
                                  disabled={hasPermissionView || isDisablePage}
                                />
                              </div>
                              <div className={clsx('mt-4', {'d-flex flex-wrap': !isDisablePage})}>
                                <InputTextArea
                                  className={clsx('col-12 col-sm-8 h-210', {
                                    'col-12': isDisablePage,
                                  })}
                                  labelClassName='col-12 col-sm-4 col-form-label text-nowrap text-muted'
                                  label={intl.formatMessage({id: 'ORDER_NOTE'})}
                                  formik={formik}
                                  name='note'
                                  rows={8}
                                />
                              </div>
                              <hr className='bg-gray-600' />
                              <ItemPackages
                                formik={formik}
                                data={data}
                                handleAddNewItem={handleAddNewItem}
                                handleDeleteItem={handleDeleteItem}
                                disabled={
                                  isDisablePage ||
                                  hasPermissionView ||
                                  !isEmpty(data?.store_order_id)
                                }
                              />
                            </div>
                          </div>
                        </div>
                        <OrderChildRate
                          ref={rateRef}
                          formik={formik}
                          setValidationErrors={setValidationErrors}
                          data={data}
                          id={parentId}
                          isDisablePage={isDisablePage}
                          name={name}
                          setRateLoading={(rate) => setLoading((prev) => ({...prev, rate}))}
                          isAccept={isAccept}
                          setIsAccept={setIsAccept}
                          isErrorUpicAccept={isErrorUpicAccept}
                          setIsErrorUpicAccept={setIsErrorUpicAccept}
                          isModal
                        />
                      </div>
                    </div>
                  )
                })()
              )}
            </>
          </>
        </Modal.Body>
        <Modal.Footer>
          {parentId && loading.first ? (
            <div className='d-flex flex-fill justify-content-between'>
              <div className='btn w-75px placeholder placeholder-lg rounded-2 bg-secondary me-2' />
              <div className='btn w-75px placeholder placeholder-lg rounded-2 bg-secondary me-2' />
            </div>
          ) : (
            <div
              className={clsx('d-flex flex-fill', {
                'justify-content-between': name === OrderChildModalName.CREATE_LABEL,
                'justify-content-end': name !== OrderChildModalName.CREATE_LABEL,
                'flex-column gap-3': isTinyMobile,
              })}
            >
              <div
                className={clsx('d-flex align-items-start', {
                  'flex-row-reverse': name !== OrderChildModalName.CREATE_LABEL,
                  'w-100 flex-column-reverse gap-2': isTinyMobile,
                })}
              >
                {isStatusOpen &&
                  (name === OrderChildModalName.EDIT_ORDER ? (
                    <>
                      <Button
                        className={clsx('btn btn-sm w-100', {
                          'btn-advanced-primary': canSave && checkValidateLeft,
                          'bg-secondary text-dark cursor-no-drop opacity-75':
                            !canSave || !checkValidateLeft,
                          'ms-4': !isTinyMobile,
                        })}
                        classNameDiv={clsx({'w-100 ms-0': isTinyMobile})}
                        label={intl.formatMessage({id: 'SAVE'})}
                        loadingText={intl.formatMessage({id: 'SAVING'})}
                        event={() => {
                          setScrollToErrors(true)
                          formik.handleSubmit()
                          if (canSave) {
                            setAction('save')
                          }
                        }}
                        loading={loading.save}
                        disabled={loading.save || isDisablePage || loading.rate}
                      />
                      <Button
                        className='btn btn-sm btn-advanced-secondary w-100'
                        classNameDiv={clsx({'w-100': isTinyMobile})}
                        label={intl.formatMessage({id: 'CANCEL'})}
                        loadingText={intl.formatMessage({id: 'CANCEL'})}
                        event={handleCloseModal}
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        className={clsx('btn btn-sm w-100', {
                          'btn-advanced-primary': canSave && checkValidateLeft,
                          'bg-secondary text-dark cursor-no-drop opacity-75':
                            !canSave || !checkValidateLeft,
                        })}
                        classNameDiv={clsx({'w-100': isTinyMobile})}
                        label={intl.formatMessage({id: 'SAVE_CHANGES'})}
                        loadingText={intl.formatMessage({id: 'SAVING'})}
                        event={() => {
                          setScrollToErrors(true)
                          formik.handleSubmit()
                          if (canSave) {
                            setAction('save')
                          }
                        }}
                        loading={loading.save}
                        disabled={loading.save || isDisablePage || loading.rate}
                      />
                    </>
                  ))}
              </div>
              {name === OrderChildModalName.CREATE_LABEL && (
                <div
                  className={clsx('d-flex justify-content-end', {
                    'w-100 ms-0 flex-column gap-2': isTinyMobile,
                    'ms-4': !isTinyMobile,
                  })}
                >
                  {parentId && routes.SHIPMENTS_CREATE.hasPermission && (
                    <>
                      <Button
                        className={clsx('btn btn-sm btn-advanced-secondary fw-bolder me-4', {
                          ' w-100 me-0': isTinyMobile,
                        })}
                        classNameDiv={clsx({'w-100': isTinyMobile})}
                        label={intl.formatMessage({id: 'VERIFY_ADDRESS'})}
                        loadingText={intl.formatMessage({id: 'VERIFY_ADDRESS'})}
                        loading={loading.validate}
                        disabled={loading.validate}
                        event={() => {
                          setAction('validate')
                          formik.submitForm()
                        }}
                      />
                      {isUsps ? (
                        <div className={clsx({'w-100': isTinyMobile})}>
                          <SplitButtonDropdown
                            fullWidth={isTinyMobile}
                            label={
                              selectedMode === 'manifest'
                                ? intl.formatMessage({id: 'CREATE_LABEL_AND_MANIFEST'})
                                : intl.formatMessage({id: 'CREATE_LABEL'})
                            }
                            options={[
                              {
                                id: 'label',
                                label: intl.formatMessage({id: 'CREATE_LABEL'}),
                              },
                              {
                                id: 'manifest',
                                label: intl.formatMessage({id: 'CREATE_LABEL_AND_MANIFEST'}),
                              },
                            ]}
                            selectedOption={selectedMode}
                            onSelectOption={(id) => {
                              setSelectedMode(id as 'label' | 'manifest')
                              setImmediateManifestFlag(id === 'manifest')
                            }}
                            onMainClick={() => {
                              setImmediateManifestFlag(selectedMode === 'manifest')
                              setScrollToErrors(true)
                              formik.handleSubmit()
                              if (!(!checkValidateLeft || !formik.values.service)) {
                                if (!rateRef.current?.serviceHasPackage()) {
                                  setShowModal((prev) => ({...prev, noticeCreateLabel: true}))
                                } else {
                                  setAction('ship')
                                }
                              }
                            }}
                            loading={loading.ship}
                            disabled={loading.save || isDisablePage}
                            icon={
                              <KTSVG
                                path='/media/gori/orders/upload_create.svg'
                                className='svg-icon-2 text-white'
                              />
                            }
                          />
                        </div>
                      ) : (
                        <Button
                          className={clsx('btn btn-sm fw-bolder w-100', {
                            'btn-advanced-primary': !(!checkValidateLeft || !formik.values.service),
                            'btn-advanced-primary cursor-no-drop':
                              !checkValidateLeft || !formik.values.service,
                          })}
                          classNameDiv={clsx({'w-100': isTinyMobile})}
                          label={intl.formatMessage({id: 'CREATE_LABEL'})}
                          loadingText={intl.formatMessage({id: 'CREATE_LABEL'})}
                          event={() => {
                            setImmediateManifestFlag(false)
                            setScrollToErrors(true)
                            formik.handleSubmit()
                            if (!(!checkValidateLeft || !formik.values.service)) {
                              if (!rateRef.current?.serviceHasPackage()) {
                                setShowModal((prev) => ({...prev, noticeCreateLabel: true}))
                              } else {
                                setAction('ship')
                              }
                            }
                          }}
                          icon={
                            <KTSVG
                              path='/media/gori/orders/upload_create.svg'
                              className={clsx('svg-icon-2')}
                            />
                          }
                          loading={loading.ship}
                          disabled={loading.save || isDisablePage}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {OrderChildModal}
